import { FilterOption } from '@/common/presentation/components/EntityFilterSubmenu';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/**
 * Props for the useEquipmentFilters hook
 *
 * Defines the input parameters required for the useEquipmentFilters hook.
 * Each property contains an array of available filter options for different equipment attributes.
 *
 * @interface UseEquipmentFiltersProps
 */
interface UseEquipmentFiltersProps {
  /**
   * Available equipment makes for filtering
   * @example [{ id: 1, name: 'Caterpillar' }, { id: 2, name: 'Komatsu' }]
   */
  availableMakes: Array<{ id?: number | null; name?: string | null | undefined }>;

  /**
   * Available equipment models for filtering
   * @example [{ id: 1, name: '320' }, { id: 2, name: 'D65' }]
   */
  availableModels: Array<{ id?: number | null; name?: string | null | undefined }>;

  /**
   * Available equipment years for filtering
   * @example [{ id: 2020, name: '2020' }, { id: 2021, name: '2021' }]
   */
  availableYears: Array<{ id?: number | null; name?: string | null | undefined }>;

  /**
   * Available equipment classes for filtering
   * @example [{ id: 1, name: 'Excavator' }, { id: 2, name: 'Bulldozer' }]
   */
  availableClasses: Array<{ id?: number | null; name?: string | null | undefined }>;

  /**
   * Available equipment statuses for filtering
   * @example [{ id: 1, name: 'Available' }, { id: 2, name: 'In Service' }]
   */
  availableStatuses: Array<{ id?: number | null; name?: string | null | undefined }>;
}

/**
 * Creates filter options from available data
 *
 * Transforms raw data objects into FilterOption objects suitable for use in filter components.
 * Filters out items with null or undefined IDs and provides default values for missing names.
 *
 * @param options - Array of objects with id and name properties
 * @returns Array of FilterOption objects with checked state initialized to false
 *
 * @example
 * ```tsx
 * const rawData = [
 *   { id: 1, name: 'Caterpillar' },
 *   { id: 2, name: 'Komatsu' },
 *   { id: null, name: 'Invalid' } // This will be filtered out
 * ];
 *
 * const filterOptions = createFilterOptions(rawData);
 * // Result: [
 * //   { id: 1, name: 'Caterpillar', checked: false },
 * //   { id: 2, name: 'Komatsu', checked: false }
 * // ]
 * ```
 */
const createFilterOptions = (options: Array<{ id?: number | null; name?: string | null | undefined }>): FilterOption[] => {
  return options
    .filter((item) => item.id != null)
    .map((option) => ({
      id: option.id!,
      name: option.name ?? '',
      checked: false
    }));
};

/**
 * Filter state management with separate applied and pending states
 *
 * Creates a complete filter state management system for a single filter category.
 * Manages both applied and pending states, provides handlers for individual and bulk operations,
 * and tracks selection states for UI components.
 *
 * @param options - Initial filter options
 * @returns Object containing filter states, handlers, and aggregated counts
 * @returns {FilterOption[]} returns.filters - Currently applied filters
 * @returns {FilterOption[]} returns.pendingFilters - Pending filter changes
 * @returns {boolean} returns.allChecked - Whether all filters are selected
 * @returns {boolean} returns.someChecked - Whether some (but not all) filters are selected
 * @returns {Function} returns.handleFilterChange - Handler for individual filter changes
 * @returns {Function} returns.handleParentCheck - Handler for select all/none
 * @returns {Function} returns.applyFilters - Handler to apply pending filters
 * @returns {Function} returns.handleClearFilters - Handler to clear all filters
 *
 * @example
 * ```tsx
 * const filterState = useCreateFilterState([
 *   { id: 1, name: 'Option 1', checked: false },
 *   { id: 2, name: 'Option 2', checked: false }
 * ]);
 *
 * // Toggle individual filter
 * filterState.handleFilterChange(1);
 *
 * // Select all/none
 * filterState.handleParentCheck();
 *
 * // Apply pending changes
 * filterState.applyFilters();
 *
 * // Clear all filters
 * filterState.handleClearFilters();
 * ```
 */
const useCreateFilterState = (options: FilterOption[]) => {
  // Applied filters - used for actual query
  const [appliedFilters, setAppliedFilters] = useState<FilterOption[]>(options);
  // Pending filters - used for UI state before applying
  const [pendingFilters, setPendingFilters] = useState<FilterOption[]>(options);
  // Track previous options to detect changes
  const prevOptionsRef = useRef<FilterOption[]>(options);

  // Helper to merge new options with existing checked state
  const mergeOptionsWithState = (newOptions: FilterOption[], currentState: FilterOption[]): FilterOption[] => {
    const stateMap = new Map(currentState.map((opt) => [opt.id, opt]));
    return newOptions.map((opt) => ({ ...opt, checked: stateMap.get(opt.id)?.checked ?? false }));
  };

  // Update state when options change (e.g., when data loads from backend)
  useEffect(() => {
    // Simple comparison: check if options array reference or content changed
    const prevOptionIds = prevOptionsRef.current.map((opt) => opt.id).join(',');
    const newOptionIds = options.map((opt) => opt.id).join(',');
    const optionsChanged = options.length !== prevOptionsRef.current.length || prevOptionIds !== newOptionIds;

    if (optionsChanged) {
      setPendingFilters((prev) => mergeOptionsWithState(options, prev));
      setAppliedFilters((prev) => mergeOptionsWithState(options, prev));
      prevOptionsRef.current = options;
    }
  }, [options]);

  /**
   * Handles individual filter option changes
   * @param optionId - ID of the filter option to toggle
   */
  const handleFilterChange = useCallback((optionId: number) => {
    setPendingFilters((prev) => prev.map((option) => (option.id === optionId ? { ...option, checked: !option.checked } : option)));
  }, []);

  /**
   * Applies pending filters to the actual filter state
   */
  const applyFilters = useCallback(() => {
    setPendingFilters((currentPending) => {
      setAppliedFilters(currentPending);
      return currentPending;
    });
  }, []);

  /**
   * Clears all filters by setting checked to false
   */
  const handleClearFilters = useCallback(() => {
    setPendingFilters((prev) => {
      const clearedFilters = prev.map((option) => ({ ...option, checked: false }));
      setAppliedFilters(clearedFilters);
      return clearedFilters;
    });
  }, []);

  /** Whether all filters in this category are checked */
  const allChecked = useMemo(() => appliedFilters.length > 0 && appliedFilters.every((f) => f.checked), [appliedFilters]);

  /** Whether some (but not all) filters in this category are checked */
  const someChecked = useMemo(() => appliedFilters.some((f) => f.checked) && !allChecked, [appliedFilters, allChecked]);

  /**
   * Handles parent checkbox (select all/none) functionality
   */
  const handleParentCheck = useCallback(() => {
    const newChecked = !allChecked;
    setPendingFilters((prev) => {
      const updatedFilters = prev.map((f) => ({ ...f, checked: newChecked }));
      setAppliedFilters(updatedFilters);
      return updatedFilters;
    });
  }, [allChecked]);

  return {
    filters: appliedFilters,
    pendingFilters,
    allChecked,
    someChecked,
    handleFilterChange,
    handleParentCheck,
    applyFilters,
    handleClearFilters
  };
};

/**
 * Hook for managing equipment filter states with pending/applied pattern
 *
 * This hook provides a comprehensive filter management system for equipment lists,
 * supporting multiple filter categories (make, model, year, class, status) with
 * separate pending and applied states. The pending state allows users to make
 * multiple filter changes before applying them, while the applied state is used
 * for actual data filtering.
 *
 * ## Features
 * - **Dual State Management**: Separate pending and applied filter states
 * - **Category Support**: Handles make, model, year, class, and status filters
 * - **Bulk Operations**: Select all/none functionality for each category
 * - **Active Count Tracking**: Tracks total number of active filters
 * - **Memoized Performance**: Optimized to prevent unnecessary re-renders
 *
 * ## State Pattern
 * - **Applied Filters**: Used for actual data filtering and API queries
 * - **Pending Filters**: Used for UI state before user confirms changes
 * - **Parent Checkboxes**: Manage select all/none for each category
 *
 * @param props - Available filter options for each equipment attribute
 * @param props.availableMakes - Array of available equipment makes with id and name
 * @param props.availableModels - Array of available equipment models with id and name
 * @param props.availableYears - Array of available equipment years with id and name
 * @param props.availableClasses - Array of available equipment classes with id and name
 * @param props.availableStatuses - Array of available equipment statuses with id and name
 *
 * @returns Object containing filter states, handlers, and aggregated counts
 * @returns {FilterOption[]} returns.makeFilters - Currently applied make filters
 * @returns {FilterOption[]} returns.pendingMakeFilters - Pending make filter changes
 * @returns {boolean} returns.allMakesChecked - Whether all make filters are selected
 * @returns {boolean} returns.someMakesChecked - Whether some (but not all) make filters are selected
 * @returns {Function} returns.handleMakeFilterChange - Handler for individual make filter changes
 * @returns {Function} returns.handleMakeParentCheck - Handler for make select all/none
 * @returns {FilterOption[]} returns.modelFilters - Currently applied model filters
 * @returns {FilterOption[]} returns.pendingModelFilters - Pending model filter changes
 * @returns {boolean} returns.allModelsChecked - Whether all model filters are selected
 * @returns {boolean} returns.someModelsChecked - Whether some (but not all) model filters are selected
 * @returns {Function} returns.handleModelFilterChange - Handler for individual model filter changes
 * @returns {Function} returns.handleModelParentCheck - Handler for model select all/none
 * @returns {FilterOption[]} returns.yearFilters - Currently applied year filters
 * @returns {FilterOption[]} returns.pendingYearFilters - Pending year filter changes
 * @returns {boolean} returns.allYearsChecked - Whether all year filters are selected
 * @returns {boolean} returns.someYearsChecked - Whether some (but not all) year filters are selected
 * @returns {Function} returns.handleYearFilterChange - Handler for individual year filter changes
 * @returns {Function} returns.handleYearParentCheck - Handler for year select all/none
 * @returns {FilterOption[]} returns.classFilters - Currently applied class filters
 * @returns {FilterOption[]} returns.pendingClassFilters - Pending class filter changes
 * @returns {boolean} returns.allClassesChecked - Whether all class filters are selected
 * @returns {boolean} returns.someClassesChecked - Whether some (but not all) class filters are selected
 * @returns {Function} returns.handleClassFilterChange - Handler for individual class filter changes
 * @returns {Function} returns.handleClassParentCheck - Handler for class select all/none
 * @returns {FilterOption[]} returns.statusFilters - Currently applied status filters
 * @returns {FilterOption[]} returns.pendingStatusFilters - Pending status filter changes
 * @returns {boolean} returns.allStatusesChecked - Whether all status filters are selected
 * @returns {boolean} returns.someStatusesChecked - Whether some (but not all) status filters are selected
 * @returns {Function} returns.handleStatusFilterChange - Handler for individual status filter changes
 * @returns {Function} returns.handleStatusParentCheck - Handler for status select all/none
 * @returns {number} returns.activeFilterCount - Total number of active filters across all categories
 * @returns {Function} returns.handleClearFilters - Handler to clear all filters
 * @returns {Function} returns.applyFilters - Handler to apply all pending filters
 *
 * @example
 * ```tsx
 * const {
 *   // Make filters
 *   makeFilters,
 *   pendingMakeFilters,
 *   allMakesChecked,
 *   someMakesChecked,
 *   handleMakeFilterChange,
 *   handleMakeParentCheck,
 *
 *   // Model filters
 *   modelFilters,
 *   pendingModelFilters,
 *   allModelsChecked,
 *   someModelsChecked,
 *   handleModelFilterChange,
 *   handleModelParentCheck,
 *
 *   // Year filters
 *   yearFilters,
 *   pendingYearFilters,
 *   allYearsChecked,
 *   someYearsChecked,
 *   handleYearFilterChange,
 *   handleYearParentCheck,
 *
 *   // Class filters
 *   classFilters,
 *   pendingClassFilters,
 *   allClassesChecked,
 *   someClassesChecked,
 *   handleClassFilterChange,
 *   handleClassParentCheck,
 *
 *   // Status filters
 *   statusFilters,
 *   pendingStatusFilters,
 *   allStatusesChecked,
 *   someStatusesChecked,
 *   handleStatusFilterChange,
 *   handleStatusParentCheck,
 *
 *   // Aggregated data
 *   activeFilterCount,
 *   handleClearFilters,
 *   applyFilters
 * } = useEquipmentFilters({
 *   availableMakes: [{ id: 1, name: 'Caterpillar' }, { id: 2, name: 'Komatsu' }],
 *   availableModels: [{ id: 1, name: '320' }, { id: 2, name: 'D65' }],
 *   availableYears: [{ id: 2020, name: '2020' }, { id: 2021, name: '2021' }],
 *   availableClasses: [{ id: 1, name: 'Excavator' }, { id: 2, name: 'Bulldozer' }],
 *   availableStatuses: [{ id: 1, name: 'Available' }, { id: 2, name: 'In Service' }]
 * });
 *
 * // Handle individual filter changes
 * const handleMakeChange = (makeId: number) => {
 *   handleMakeFilterChange(makeId);
 * };
 *
 * // Apply pending changes
 * const handleApply = () => {
 *   applyFilters();
 * };
 *
 * // Clear all filters
 * const handleClear = () => {
 *   handleClearFilters();
 * };
 * ```
 */
export const useEquipmentFilters = ({
  availableMakes,
  availableModels,
  availableYears,
  availableClasses,
  availableStatuses
}: UseEquipmentFiltersProps) => {
  // Memoize filter options to prevent recreation on every render
  const makeOptions = useMemo(() => createFilterOptions(availableMakes), [availableMakes]);
  const modelOptions = useMemo(() => createFilterOptions(availableModels), [availableModels]);
  const yearOptions = useMemo(() => createFilterOptions(availableYears), [availableYears]);
  const classOptions = useMemo(() => createFilterOptions(availableClasses), [availableClasses]);
  const statusOptions = useMemo(() => createFilterOptions(availableStatuses), [availableStatuses]);

  // Create filter state for each category
  const makeState = useCreateFilterState(makeOptions);
  const modelState = useCreateFilterState(modelOptions);
  const yearState = useCreateFilterState(yearOptions);
  const classState = useCreateFilterState(classOptions);
  const statusState = useCreateFilterState(statusOptions);

  /**
   * Clears all filters across all categories
   */
  const handleClearFilters = useCallback(() => {
    makeState.handleClearFilters();
    modelState.handleClearFilters();
    yearState.handleClearFilters();
    classState.handleClearFilters();
    statusState.handleClearFilters();
  }, [
    makeState.handleClearFilters,
    modelState.handleClearFilters,
    yearState.handleClearFilters,
    classState.handleClearFilters,
    statusState.handleClearFilters
  ]);

  /**
   * Applies all pending filters across all categories
   */
  const applyFilters = useCallback(() => {
    makeState.applyFilters();
    modelState.applyFilters();
    yearState.applyFilters();
    classState.applyFilters();
    statusState.applyFilters();
  }, [makeState.applyFilters, modelState.applyFilters, yearState.applyFilters, classState.applyFilters, statusState.applyFilters]);

  return {
    // Make filters
    makeFilters: makeState.filters,
    pendingMakeFilters: makeState.pendingFilters,
    allMakesChecked: makeState.allChecked,
    someMakesChecked: makeState.someChecked,
    handleMakeFilterChange: makeState.handleFilterChange,
    handleMakeParentCheck: makeState.handleParentCheck,

    // Model filters
    modelFilters: modelState.filters,
    pendingModelFilters: modelState.pendingFilters,
    allModelsChecked: modelState.allChecked,
    someModelsChecked: modelState.someChecked,
    handleModelFilterChange: modelState.handleFilterChange,
    handleModelParentCheck: modelState.handleParentCheck,

    // Year filters
    yearFilters: yearState.filters,
    pendingYearFilters: yearState.pendingFilters,
    allYearsChecked: yearState.allChecked,
    someYearsChecked: yearState.someChecked,
    handleYearFilterChange: yearState.handleFilterChange,
    handleYearParentCheck: yearState.handleParentCheck,

    // Class filters
    classFilters: classState.filters,
    pendingClassFilters: classState.pendingFilters,
    allClassesChecked: classState.allChecked,
    someClassesChecked: classState.someChecked,
    handleClassFilterChange: classState.handleFilterChange,
    handleClassParentCheck: classState.handleParentCheck,

    // Status filters
    statusFilters: statusState.filters,
    pendingStatusFilters: statusState.pendingFilters,
    allStatusesChecked: statusState.allChecked,
    someStatusesChecked: statusState.someChecked,
    handleStatusFilterChange: statusState.handleFilterChange,
    handleStatusParentCheck: statusState.handleParentCheck,

    // Active filter count
    activeFilterCount: useMemo(
      () =>
        makeState.filters.filter((f) => f.checked).length +
        modelState.filters.filter((f) => f.checked).length +
        yearState.filters.filter((f) => f.checked).length +
        classState.filters.filter((f) => f.checked).length +
        statusState.filters.filter((f) => f.checked).length,
      [makeState.filters, modelState.filters, yearState.filters, classState.filters, statusState.filters]
    ),

    // Combined clear and apply functions
    handleClearFilters,
    applyFilters
  };
};
